import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Send } from 'lucide-react';
import { getDisplayErrorMessage } from '@/lib/api';
import {
  changedFields, discardOutreach, draftOutreach, editsOf, getOutreach, isOnTo, listOutreach, outcomeLine, resultLabel, saveOutreach, sendLabel, sendOutreach, toggleRecipient,
  type OutreachEdits, type OutreachSourceKind, type OutreachView,
} from '@/lib/propertyOutreach';
import formStyles from './Form.module.css';
import styles from './ContactProperty.module.css';

/**
 * Contact the property (Warren, 4 Nov 2026: "EVI chooses when to reach out ... Nothing goes out without an EVI person
 * sending it"; ruling R10). The card on an alert and a service call; the page at /property-outreach/:id shows the same
 * editor for the breaker step's draft from the queue.
 *
 * The parent shows the card on the Api's own flag (can_contact_property on an alert, can.contact_property on a service
 * call), never on a persona. A draft goes to the location's contacts: tick or untick them here; the Api refuses any other
 * address. Send saves the edits first, then sends, and the Api holds a double click to one send.
 */
const when = (iso: string) => new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

/** A draft that went or was dropped: what was said, and to whom. */
export function OutreachRecord({ draft }: { draft: OutreachView }) {
  return (
    <div className={styles.record}>
      <p className={styles.line}><strong>{draft.subject}</strong></p>
      <p className={styles.hint}>{outcomeLine(draft, when)}</p>
      {draft.results.length > 0 && (
        <ul className={styles.results}>
          {draft.results.map((r) => <li key={`${r.email}-${r.at}`}>{r.email}: {resultLabel(r.result)}</li>)}
        </ul>
      )}
      <details>
        <summary className={styles.hint}>What it said</summary>
        <p className={styles.message}>{draft.body}</p>
      </details>
    </div>
  );
}

/** An open draft: who it goes to, the subject and the message, and the press that sends it. */
export function OutreachEditor({ draft, onChange }: { draft: OutreachView; onChange: (next: OutreachView) => void }) {
  const [edits, setEdits] = useState<OutreachEdits>(() => editsOf(draft));
  const [busy, setBusy] = useState(false), [error, setError] = useState(''), [saved, setSaved] = useState(false);
  // A newer copy from the Api (after a save, or a refused send) is the truth.
  useEffect(() => { setEdits(editsOf(draft)); }, [draft.id, draft.updated_at]);

  const changes = changedFields(draft, edits);
  const dirty = Object.keys(changes).length > 0;
  const set = (patch: Partial<OutreachEdits>) => { setEdits((e) => ({ ...e, ...patch })); setSaved(false); };

  async function save(): Promise<OutreachView | null> {
    if (!dirty) return draft;
    try { return await saveOutreach(draft.id, changes); }
    catch (err) { setError(await getDisplayErrorMessage(err, 'The draft was not saved.')); return null; }
  }
  async function run(fn: () => Promise<void>) {
    setBusy(true); setError(''); setSaved(false);
    try { await fn(); } finally { setBusy(false); }
  }
  const saveOnly = () => run(async () => { const next = await save(); if (next) { onChange(next); setSaved(true); } });
  const send = () => run(async () => {
    const next = await save();
    if (!next) return;
    try { onChange(await sendOutreach(draft.id)); }
    catch (err) {
      setError(await getDisplayErrorMessage(err, 'The email did not go. Nothing was sent, so you can try again.'));
      // It may have moved on the Api's side: a second press elsewhere, or the charger came back and the ask closed.
      const latest = await getOutreach(draft.id).catch(() => null);
      if (latest) onChange(latest);
    }
  });
  const discard = () => run(async () => {
    try { onChange(await discardOutreach(draft.id)); }
    catch (err) { setError(await getDisplayErrorMessage(err, 'The draft was not dropped.')); }
  });

  return (
    <>
      {draft.warning && <p className={styles.warning} role="status">{draft.warning}</p>}
      <fieldset className={styles.recipients} disabled={busy}>
        <legend className={styles.legend}>To: the people EVI contacts about {draft.location.name || 'this location'}</legend>
        {draft.contacts.map((c) => (
          <label key={c.email.toLowerCase()} className={styles.recipient}>
            <input type="checkbox" checked={isOnTo(edits.to, c.email)} onChange={(e) => set({ to: toggleRecipient(edits.to, c.email, e.target.checked) })} />
            <span>{c.name || c.email}{c.name && <span className={styles.address}> {c.email}</span>}</span>
          </label>
        ))}
      </fieldset>
      <label className={styles.field}>
        <span>Subject</span>
        <input className={formStyles.input} value={edits.subject} maxLength={300} disabled={busy} onChange={(e) => set({ subject: e.target.value })} />
      </label>
      <label className={styles.field}>
        <span>Message</span>
        <textarea className={`${formStyles.input} ${styles.body}`} value={edits.body} maxLength={10_000} disabled={busy} onChange={(e) => set({ body: e.target.value })} />
      </label>
      <p className={styles.hint}>
        Nothing goes out until you press Send. It comes from EV Initiative, and replies go to support@evinitiative.com.
        {draft.done_link_appended ? " Each person's own Done link is added to their copy when you send." : ''}
      </p>
      {error && <div className={styles.alertErr} role="alert">{error}</div>}
      {saved && !error && <div className={styles.alertOk} role="status">Saved. Nothing has gone out.</div>}
      <div className={styles.actions}>
        <button type="button" className={formStyles.btnPrimary} disabled={busy || edits.to.length === 0 || !draft.can.edit} onClick={() => void send()}>
          <Send size={13} /> {busy ? 'Working' : sendLabel(edits.to.length)}
        </button>
        <button type="button" className={formStyles.btnSecondary} disabled={busy || !dirty} onClick={() => void saveOnly()}>Save the draft</button>
        <button type="button" className={formStyles.btnSecondary} disabled={busy || !draft.can.discard} onClick={() => void discard()}>Drop it</button>
      </div>
    </>
  );
}

export function ContactPropertyCard({ sourceKind, sourceId }: { sourceKind: Exclude<OutreachSourceKind, 'breaker_step'>; sourceId: string }) {
  const client = useQueryClient();
  const key = ['property-outreach', sourceKind, sourceId];
  const q = useQuery({ queryKey: key, queryFn: () => listOutreach(sourceKind, sourceId), retry: false });
  const [busy, setBusy] = useState(false), [error, setError] = useState('');
  const refresh = () => client.invalidateQueries({ queryKey: key });

  async function start() {
    setBusy(true); setError('');
    try { await draftOutreach(sourceKind, sourceId); await refresh(); }
    catch (err) { setError(await getDisplayErrorMessage(err, 'The draft was not made.')); } finally { setBusy(false); }
  }
  // The Api refused the list (the flag said yes a moment ago): nothing to offer.
  if (q.isError) return null;
  const rows = q.data ?? [];
  const open = rows.find((d) => d.status === 'draft');
  const earlier = rows.filter((d) => d.status !== 'draft');
  return (
    <section className={styles.card} aria-labelledby="contact-property-title">
      <h2 id="contact-property-title" className={styles.title}><Send size={15} /> Contact the property</h2>
      {!open && <p className={styles.hint}>Drafts an email to this location&apos;s contacts for you to edit and send. Nothing goes out until you press Send.</p>}
      {error && <div className={styles.alertErr} role="alert">{error}</div>}
      {open
        ? <OutreachEditor key={open.id} draft={open} onChange={() => { void refresh(); }} />
        : <div className={styles.actions}><button type="button" className={formStyles.btnSecondary} disabled={busy || q.isLoading} onClick={() => void start()}>{busy ? 'Drafting' : 'Contact the property'}</button></div>}
      {earlier.map((d) => <OutreachRecord key={d.id} draft={d} />)}
    </section>
  );
}
